import config from 'config';
import { IntlShape, MessageDescriptor } from 'react-intl';

const DOT_TEXT_TO_IGNORE = config.textToSpeech.dotTextToIgnore;

export const monthNames = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
};

const messages: Record<string, MessageDescriptor> = {
  of: {
    id: 'textToSpeechHelper.of',
    description: 'of',
  },
  minutes: {
    id: 'textToSpeechHelper.minutes',
    description: 'Minutes',
  },
  anteMeridiem: {
    id: 'textToSpeechHelper.anteMeridiem',
    description: 'ae m',
  },
  and: {
    id: 'textToSpeechHelper.and',
    description: 'and',
  },
  slash: {
    id: 'textToSpeechHelper.slash',
    description: 'slash',
  },
  dash: {
    id: 'textToSpeechHelper.dash',
    description: 'dash',
  },
  dot: {
    id: 'textToSpeechHelper.dot',
    description: 'dot',
  },
  number: {
    id: 'textToSpeechHelper.number',
    description: 'number',
  },
  duration: {
    id: 'textToSpeechHelper.duration',
    description: 'duration',
  },
  zeroDuration: {
    id: 'textToSpeechHelper.zeroSeconds',
    description: 'zero seconds',
  },
  colon: {
    id: 'textToSpeechHelper.colon',
    description: 'colon',
  },
  uefa: {
    id: 'textToSpeechHelper.uefa',
    description: 'UEFA',
  },
  afc: {
    id: 'textToSpeechHelper.afc',
    description: 'AFC',
  },
};

export const getAmpersandText = (text: string, intl: IntlShape): string =>
  text && typeof text === 'string'
    ? text.replace(/\s&\s/g, ` ${intl.formatMessage(messages.and)} `)
    : text;

export const getSplitChars = (text: string): string =>
  text && typeof text === 'string' ? text.split('').join(', ') : text;

export const getTimeText = (text: string, intl: IntlShape): string =>
  text && typeof text === 'string'
    ? text
        .replace(/:[0]{2}/g, '')
        .replace(/\bam\b/gi, intl.formatMessage(messages.anteMeridiem))
    : text;

export const removeColons = (text: string): string =>
  text && typeof text === 'string' ? text.replace(':', ' ') : text;

export const getDotToIgnoreText = (text: string): string => {
  let result = text;
  if (result && typeof result === 'string') {
    DOT_TEXT_TO_IGNORE.forEach(dotText => {
      const replacement = dotText.replace(/\./g, '');
      const regex = new RegExp(dotText, 'gm');
      result = result.replace(regex, replacement);
    });
  }
  return result;
};

export const getDotText = (text: string, intl: IntlShape): string =>
  text && typeof text === 'string'
    ? text.replace(/\b\.\b/gm, ` ${intl.formatMessage(messages.dot)} `)
    : text;

export const getSlashText = (text: string, intl: IntlShape): string =>
  text && typeof text === 'string'
    ? text.replace(/\b\//gm, ` ${intl.formatMessage(messages.slash)} `)
    : text;

export const getDashText = (text: string, intl: IntlShape): string =>
  text && typeof text === 'string'
    ? text.replace(/-/gm, ` ${intl.formatMessage(messages.dash)} `)
    : text;

export const getHttpPunctuationText = (text: string, intl: IntlShape): string => {
  const colon = intl.formatMessage(messages.colon);
  const slash = intl.formatMessage(messages.slash);
  return text && typeof text === 'string'
    ? text.replace(/(https|http)(:\/\/)/gim, `$1 ${colon} ${slash} ${slash} `)
    : text;
};

export const getAllCapsText = (text: string, wordList: string[] | null | undefined = null): string => {
  if (text && typeof text === 'string') {
    const regex = /\w[A-Z]+/gm;
    const found = text.match(regex);

    if (found !== null && found.length > 0) {
      let wordText = text;
      found.forEach(allCaps => {
        if (wordList && wordList.length > 0) {
          if (wordList.includes(allCaps)) {
            wordText = wordText.replace(allCaps, allCaps.split('').join(' '));
          }
        } else if (!wordList || wordList.length === 0) {
          wordText = wordText.replace(allCaps, allCaps.split('').join(' '));
        }
      });
      return wordText;
    }
  }
  return text;
};

interface LegendItem {
  ttsButton?: string;
  textId: string;
}

export const getFilteredLegendTTS = (filteredLegend: LegendItem[], intl: IntlShape): string[] => {
  return filteredLegend.map(item => {
    if (item.ttsButton) {
      return `${intl.formatMessage({
        id: item.ttsButton,
      })} ${intl.formatMessage({
        id: item.textId,
      })}`;
    }
    return intl.formatMessage({
      id: item.textId,
    });
  });
};

export const getCBSText = (text: string): string =>
  text && typeof text === 'string' ? text.replace(/cbs/gim, ' c b s ') : text;

export const getHTMLFilteredText = (text: string): string => {
  if (text && typeof text === 'string') {
    const regex = /(<([^>]+)>)/gi;
    const found = text.match(regex);

    if (found !== null && found.length > 0) {
      let wordText = text;
      found.forEach(htmlTag => {
        wordText = wordText.replace(htmlTag, '');
      });
      return wordText;
    }
  }
  return text;
};

// Handle the cases that "No.7" is meant to be "Number 7", usually for college events
export const getNoAsNumberText = (text: string, intl: IntlShape): string => {
  if (text && typeof text === 'string') {
    return text.replace(
      /(No\.)(\s?[0-9]+)/gi,
      `${intl.formatMessage(messages.number, { number: `$2` })}`,
    );
  }

  return text;
};

export const getOverrides = (text: string, intl: IntlShape, overrides: Record<string, MessageDescriptor>): string => {
  try {
    if (overrides instanceof Object && Object.keys(overrides).length > 0) {
      let newText = text;
      Object.entries(overrides).forEach(([key, value]) => {
        const reg = new RegExp(`\\b${key}\\b`, 'gi');
        newText = newText.replace(reg, ` ${intl.formatMessage(value)} `);
      });

      return newText;
    }
  } catch {
    return text;
  }

  return text;
};

export const getInitialOverrides = (text: string, intl: IntlShape): string => {
  const overrides: Record<string, MessageDescriptor> = {};

  return getOverrides(text, intl, overrides);
};

export const getFinalOverrides = (text: string, intl: IntlShape): string => {
  const overrides = {
    afc: messages.afc,
    uefa: messages.uefa,
  };

  return getOverrides(text, intl, overrides);
};

export const getTileTTS = ({ about }: { about: { title: string } }): string => about.title;

export const getDurationTTS = (
  durationInSeconds: number,
  intl: IntlShape,
  allowZeroSeconds: boolean = false,
): string => {
  if (durationInSeconds === 0 && allowZeroSeconds) {
    return intl.formatMessage(messages.zeroDuration);
  }

  const seconds = Math.floor(durationInSeconds % 60);
  const durationInMinutes = Math.floor(durationInSeconds / 60);
  const hours = Math.floor(durationInMinutes / 60);
  const minutes = Math.floor(durationInMinutes % 60);
  return intl.formatMessage(messages.duration, { hours, minutes, seconds });
};
